/*
  Flowkeeper - Pomodoro timer for power users and teams
  Copyright (c) 2023 Constantine Kulak, Geoffrey Espagne

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU General Public License as published by
  the Free Software Foundation; either version 3 of the License, or
  (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU General Public License for more details.

  You should have received a copy of the GNU General Public License
  along with this program.  If not, see <https://www.gnu.org/licenses/>.
*/

import {useContext, useRef, useState} from 'react';
import {
    ActionGroup,
    Button,
    ButtonType,
    ButtonVariant,
    Form,
    FormContextProvider,
    InputGroup,
    InputGroupItem,
    Modal,
    ModalVariant, 
    TextInput  
} from '@patternfly/react-core';
import EyeIcon from '@patternfly/react-icons/dist/esm/icons/eye-icon';
import EyeSlashIcon from '@patternfly/react-icons/dist/esm/icons/eye-slash-icon';

import {useNavigate} from 'react-router-dom';
import {disconnectEventSource} from '../../fk/core/websocket_event_source'

import DataContext from '../context/DataContext';
import ConnectionContext from "../context/ConnectionContext";

const Cookies = require("js-cookie");

type SignOutPageProps = {
    isOpen: boolean,
    setIsOpen: Function
}

SignOutPage.defaultProps = {
    isOpen: false,
    setIsOpen: () => {}
}

function SignOutPage(props: SignOutPageProps) {

    const navigate = useNavigate();

    const { setConnection } = useContext(ConnectionContext);
    const { setData } = useContext(DataContext);

    let key = Cookies.get('fw-ukey');

    const ref = useRef<HTMLInputElement>(null);

    const [isPasswordHidden, setIsPasswordHidden] = useState(true);

    const signOut = () => {
        Cookies.remove('fw-utoken');
        Cookies.remove('fw-uauthtype');
        Cookies.remove('fw-uavatar');
        Cookies.remove('fw-uname');
        Cookies.remove('fw-ukey');
        Cookies.remove('fw-ucachedkey');
        disconnectEventSource(setConnection, setData);
        navigate("/login");
    };

    const onCancel = (_event: KeyboardEvent | React.MouseEvent | undefined) => {
        props.setIsOpen(!props.isOpen);
    };

    return (
        <Modal
            variant={ModalVariant.medium}
            title={"Sign Out"}
            isOpen={props.isOpen}
            showClose={false}
            >
            <FormContextProvider initialValues={{'key': key}}>
                {({setValue, getValue, setError}) => (
                    <Form>
                        Please be sure to save your key, as you will need to decrypt your data in the future and will NOT be able to restore it if you forget it: 
                        <InputGroup>
                            <InputGroupItem isFill>
                                <TextInput
                                    id="key"
                                    ref={ref}
                                    isDisabled={true}
                                    onFocus={() => ref?.current?.select()}
                                    onChange={(_event, value) => {
                                        setValue('key', value);
                                        setError('key', undefined);
                                    }}
                                    type={isPasswordHidden ? 'password' : 'text'}
                                    value={getValue('key')}/>
                            </InputGroupItem>
                            <InputGroupItem>
                                <Button
                                    variant="control"
                                    onClick={() => setIsPasswordHidden(!isPasswordHidden)}
                                    aria-label={isPasswordHidden ? 'Show password' : 'Hide password'}
                                    >
                                    {isPasswordHidden ? <EyeIcon /> : <EyeSlashIcon />}
                                </Button>
                            </InputGroupItem>
                        </InputGroup>
                        <ActionGroup>
                            <Button
                                type={ButtonType.submit}
                                onClick={(e) => {
                                    e.preventDefault();   
                                    signOut();
                                }}>Sign Out</Button>
                            <Button
                                type={ButtonType.button}
                                variant={ButtonVariant.link}
                                onClick={(e) => {
                                    e.preventDefault();
                                    onCancel(e);
                                }}>Cancel</Button>
                        </ActionGroup>
                    </Form>)}
            </FormContextProvider>     
    </Modal>
    )
}

export default SignOutPage;
